import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  createTheme,
  ThemeProvider,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import LinkIcon from "@mui/icons-material/Link";
import EditIcon from "@mui/icons-material/Edit";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { base_url } from "../api/api-url";
import { useNavigate } from "react-router-dom";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

const hogistTheme = createTheme({
  palette: {
    primary: { main: "#c60800" },
    secondary: { main: "#c60800" },
  },
});

const RegisteredEventVendors = () => {
  const [rows, setRows] = useState([]);
  const navigate = useNavigate();
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("createdAt");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(null);
  const pdfRef = useRef(null);

  useEffect(() => {
    fetch(`${base_url}/event-vendor`)
      .then((r) => r.json())
      .then((res) => {
        setRows(res?.data ?? []);
        setFiltered(res?.data ?? []);
      })
      .catch((err) => console.error("Fetch error:", err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const q = search.toLowerCase();
    const f = rows.filter((v) =>
      [
        "cateringName",
        "firstName",
        "lastName",
        "email",
        "mobile",
        "city",
        "state",
      ].some((k) => (v[k] ?? "").toLowerCase().includes(q))
    );
    setFiltered(f);
  }, [search, rows]);

  const descendingComparator = (a, b, orderBy) => {
    const aVal = a?.[orderBy];
    const bVal = b?.[orderBy];
    if (orderBy === "createdAt") return new Date(bVal) - new Date(aVal);
    const av = (aVal ?? "").toString().toLowerCase();
    const bv = (bVal ?? "").toString().toLowerCase();
    if (bv < av) return -1;
    if (bv > av) return 1;
    return 0;
  };

  const getComparator = (order, orderBy) =>
    order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);

  const stableSort = (array, comparator) => {
    const stabilized = array.map((el, index) => [el, index]);
    stabilized.sort((a, b) => {
      const o = comparator(a[0], b[0]);
      if (o !== 0) return o;
      return a[1] - b[1];
    });
    return stabilized.map((el) => el[0]);
  };

  const visibleRows = useMemo(() => {
    const sorted = stableSort(filtered, getComparator(order, orderBy));
    return sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filtered, order, orderBy, page, rowsPerPage]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const downloadPDF = async () => {
    const node = pdfRef.current;
    if (!node) return;

    const imgs = node.querySelectorAll("img");
    await Promise.all(
      Array.from(imgs).map(
        (img) =>
          new Promise((resolve) => {
            if (img.complete) resolve();
            else img.onload = img.onerror = resolve;
          })
      )
    );

    const canvas = await html2canvas(node, {
      scale: 2,
      backgroundColor: "#fff",
      useCORS: true,
      logging: false,
    });

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = 0;

    while (heightLeft > 0) {
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      if (heightLeft > 0) {
        pdf.addPage();
        position = -pageHeight;
      }
    }

    pdf.save(`${current?.cateringName || "vendor"}.pdf`);
  };

  return (
    <ThemeProvider theme={hogistTheme}>
      <Stack spacing={4}>
        <Paper
          elevation={3}
          sx={{ bgcolor: "#1e1e1e", p: 2, borderRadius: 2, color: "#fff" }}
        >
          {/* ---------- HEADER ---------- */}
          <Box
            display="flex"
            justifyContent={{ xs: "center", sm: "space-between" }}
            alignItems="center"
            gap={1}
            flexWrap="wrap"
            mb={2}
          >
            <Typography variant="h5" whiteSpace="nowrap" fontWeight="bold">
              Registered Event Vendors
            </Typography>

            <Box
              display="flex"
              gap={2}
              flexWrap="wrap"
              justifyContent="center"
              alignItems="center"
            >
              <Button
                onClick={() => {
                  navigator.clipboard
                    .writeText(
                      "https://tickets.hogist.com/vendor-event-registration"
                    )
                    .then(() => alert("Link copied to clipboard!"));
                }}
                variant="contained"
                startIcon={<LinkIcon />}
              >
                Registration Link
              </Button>
              <TextField
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
                size="small"
                sx={{
                  width: { xs: "100%", sm: 250, md: 300 },
                  bgcolor: "#343a40",
                  input: { color: "#fff" },
                  borderRadius: 1,
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "#6c757d" },
                    "&:hover fieldset": { borderColor: "#adb5bd" },
                    "&.Mui-focused fieldset": { borderColor: "#90caf9" },
                  },
                }}
              />
            </Box>
          </Box>

          {/* ---------- TABLE ---------- */}
          {loading ? (
            <Box py={6} textAlign="center">
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer sx={{ backgroundColor: "transparent" }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow sx={{ background: "#8d8787" }}>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === "cateringName"}
                        direction={orderBy === "cateringName" ? order : "asc"}
                        onClick={() => handleRequestSort("cateringName")}
                      >
                        Establishment
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>City / State</TableCell>

                    <TableCell
                      sortDirection={orderBy === "createdAt" ? order : false}
                    >
                      <TableSortLabel
                        active={orderBy === "createdAt"}
                        direction={orderBy === "createdAt" ? order : "asc"}
                        onClick={() => handleRequestSort("createdAt")}
                      >
                        Created
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {visibleRows.map((v) => (
                    <TableRow key={v._id} hover>
                      <TableCell sx={{ color: "#fff" }}>
                        {v.cateringName}
                      </TableCell>
                      <TableCell sx={{ color: "#fff" }}>
                        {v.firstName} {v.lastName}
                        <br />
                        <small>{v.mobile}</small>
                      </TableCell>
                      <TableCell sx={{ color: "#fff" }}>{v.email}</TableCell>
                      <TableCell sx={{ color: "#fff" }}>
                        {v.city} / {v.state}
                      </TableCell>

                      <TableCell sx={{ color: "#fff" }}>
                        {fmtDate(v.createdAt)}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton
                            color="info"
                            onClick={() => {
                              setCurrent(v);
                              setOpen(true);
                            }}
                          >
                            <VisibilityIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() =>
                              navigate(`/vendor-event-registration/${v._id}`)
                            }
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Box
                className="mui-wrapper mui-isolated-container p-0"
                sx={{
                  color: "white",
                  px: 2,
                  py: 1,
                  display: "flex",
                  justifyContent: "flex-end",

                  borderTop: "1px solid #ccc",
                }}
              >
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={filtered.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(event, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                  }}
                  sx={{
                    color: "#fff",
                    bgcolor: "#212529",
                    ".MuiTablePagination-toolbar": {
                      color: "white",
                      overflow: {
                        xs: "scroll",
                        sm: "hidden",
                      },
                    },
                    ".MuiTablePagination-actions": { color: "#90caf9" },
                    ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":
                      { color: "#adb5bd" },
                  }}
                />
              </Box>
            </TableContainer>
          )}
        </Paper>

        {/* ---------- DIALOG ---------- */}
        <Dialog
          open={open}
          onClose={() => setOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle
            sx={{
              bgcolor: "#c60800",
              color: "white",
              fontWeight: "bold",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            Vendor Details
            <IconButton color="inherit" onClick={() => setOpen(false)}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers sx={{ backgroundColor: "#fafafa" }}>
            {current && (
              <Box ref={pdfRef}>
                <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Establishment
                        </TableCell>
                        <TableCell>{current.cateringName || "—"}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Contact Person
                        </TableCell>
                        <TableCell>
                          {`${current.firstName || ""} ${
                            current.lastName || ""
                          }` || "—"}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Designation
                        </TableCell>
                        <TableCell>{current.designation || "—"}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Mobile</TableCell>
                        <TableCell>{current.mobile || "—"}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Alternate Mobile
                        </TableCell>
                        <TableCell>{current.altMobile || "—"}</TableCell>
                      </TableRow>

                      {/* Alternate contacts */}
                      {Array.from({ length: 5 }).map((_, i) => {
                        const idx = i + 1;
                        const name = current[`person${idx}_name`];
                        const number = current[`person${idx}_number`];
                        const designation = current[`person${idx}_designation`];
                        if (!name && !number && !designation) return null;
                        return (
                          <TableRow key={idx}>
                            <TableCell sx={{ fontWeight: 600 }}>
                              Alternate Contact {idx}
                            </TableCell>
                            <TableCell>
                              {name && (
                                <div>
                                  <strong>Name:</strong> {name}
                                </div>
                              )}
                              {number && (
                                <div>
                                  <strong>Contact:</strong> {number}
                                </div>
                              )}
                              {designation && (
                                <div>
                                  <strong>Designation:</strong> {designation}
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}

                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>PAN</TableCell>
                        <TableCell>{current.pan || "—"}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>GST</TableCell>
                        <TableCell>{current.gst || "—"}</TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Kitchen Images
                        </TableCell>
                        <TableCell>
                          {current.kitchenImages?.length ? (
                            <Box
                              sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}
                            >
                              {current.kitchenImages.map((img, i) => (
                                <Link key={i} href={img} target="_blank">
                                  <img
                                    src={img}
                                    alt="Kitchen"
                                    style={{
                                      width: 120,
                                      height: 90,
                                      borderRadius: 6,
                                      objectFit: "cover",
                                    }}
                                  />
                                </Link>
                              ))}
                            </Box>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Menu List
                        </TableCell>
                        <TableCell>
                          {current.menuListUrl ? (
                            current.menuListUrl.endsWith(".pdf") ? (
                              <Link href={current.menuListUrl} target="_blank">
                                View PDF
                              </Link>
                            ) : (
                              <img
                                src={current.menuListUrl}
                                alt="Menu"
                                style={{
                                  width: 120,
                                  height: 90,
                                  borderRadius: 6,
                                  objectFit: "cover",
                                }}
                              />
                            )
                          ) : (
                            "—"
                          )}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Signature
                        </TableCell>
                        <TableCell>
                          {current.signatureUrl ? (
                            <img
                              src={current.signatureUrl}
                              alt="Signature"
                              style={{
                                width: 120,
                                height: 60,
                                borderRadius: 6,
                                objectFit: "contain",
                              }}
                            />
                          ) : (
                            "—"
                          )}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>
                          QHSC Auditor
                        </TableCell>
                        <TableCell>{current.qhscauditor || "—"}</TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Referral Name
                        </TableCell>
                        <TableCell>
                          {`${current.referralFirst || ""} ${
                            current.referralLast || ""
                          }` || "—"}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Interested Services
                        </TableCell>
                        <TableCell>
                          {current.interestedServices?.join(", ") || "—"}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                        <TableCell>{fmtDate(current.createdAt)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{ justifyContent: "center", py: 2 }}>
            <Button variant="contained" color="primary" onClick={downloadPDF}>
              Download Vendor Details
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={async () => {
                if (current?.aggreementUrl) {
                  const res = await fetch(current.aggreementUrl);
                  const blob = await res.blob();
                  const link = document.createElement("a");
                  link.href = URL.createObjectURL(blob);
                  link.download = `${
                    current.cateringName || "vendor"
                  }_agreement.pdf`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                } else alert("No agreement found.");
              }}
            >
              Download Agreement
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </ThemeProvider>
  );
};

export default RegisteredEventVendors;
